import { useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { Table, TableCard } from '@/components/application/table/table'
import { useSimBatch, useSimScenarios } from '@/hooks/api/useSimulations'
import { useAssistantsByIds } from '@/hooks/api/useAssistants'
import { useUsersByIds } from '@/hooks/api/useUsers'
import { StatusBadge, PassFailBadge, PassRateBar } from './components'
import type { SimRun } from '@/types/simulation'

/**
 * Batch detail page - shows batch info and all runs in the batch
 */
export function SimBatchDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const batchId = Number(id)

  // Fetch batch with runs (auto-polls while running)
  const { data: batch, isLoading, error } = useSimBatch({ id: batchId })
  const { data: scenarios = [] } = useSimScenarios({})

  // Extract unique assistant IDs from runs and fetch them specifically
  const assistantIds = [...new Set((batch?.runs ?? []).map((run) => run.assistant_id))]
  const { data: assistants = [] } = useAssistantsByIds(assistantIds, assistantIds.length > 0)

  // Fetch user who created this batch (if available)
  const createdByIds = batch?.created_by_id ? [batch.created_by_id] : []
  const { data: createdByUsers = [] } = useUsersByIds(createdByIds, createdByIds.length > 0)
  const createdByUser = createdByUsers[0]

  // Helper functions
  const getScenarioName = (scenarioId: number) => {
    const scenario = scenarios.find((s) => s.id === scenarioId)
    return scenario?.name ?? t('resources.simulations.scenario_fallback', { id: scenarioId })
  }

  const getAssistantName = (assistantId: number) => {
    const assistant = assistants.find((a) => a.id === assistantId)
    return assistant?.name ?? t('resources.simulations.assistant_fallback', { id: assistantId })
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const handleBack = () => {
    navigate('/simulations/batches')
  }

  const handleRunClick = useCallback(
    (run: SimRun) => {
      navigate(`/simulations/runs/${run.id}`)
    },
    [navigate]
  )

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  // Error state
  if (error || !batch) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">
          {t('pages.simulations.batch_details.not_found')}
        </p>
        <Button color="secondary" onClick={handleBack} className="mt-4">
          {t('pages.simulations.batch_details.back_to_batches')}
        </Button>
      </div>
    )
  }

  // Calculate stats
  const passRate = batch.completed_runs > 0 ? batch.passed_runs / batch.completed_runs : 0
  const progress = batch.total_runs > 0 ? batch.completed_runs / batch.total_runs : 0
  const isRunning = batch.status === 'running' || batch.status === 'pending'

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('pages.simulations.batch_details.title', { id: batch.id })}
        subtitle={
          <div className="flex items-center gap-2">
            <StatusBadge status={batch.status} />
            {isRunning && (
              <span className="flex items-center gap-1.5 text-sm text-gray-500">
                <span className="h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                {t('pages.simulations.batches.live')}
              </span>
            )}
          </div>
        }
        backButton={{
          label: t('pages.simulations.batch_details.back_to_batches'),
          onClick: handleBack,
        }}
      />

      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6 lg:p-8">
        {/* Overview Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('pages.simulations.batch_details.overview')}
          </h3>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {/* Progress */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('pages.simulations.batch_details.progress')}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {batch.completed_runs}/{batch.total_runs}
                </span>
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-full transition-all duration-300 ${
                    batch.status === 'completed'
                      ? 'bg-success-500'
                      : isRunning
                        ? 'bg-brand-500'
                        : 'bg-gray-400'
                  }`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>

            {/* Passed */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('pages.simulations.batch_details.passed')}
              </div>
              <div className="mt-2 text-2xl font-semibold text-success-600 dark:text-success-400">
                {batch.passed_runs}
              </div>
            </div>

            {/* Failed */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('pages.simulations.batch_details.failed')}
              </div>
              <div className="mt-2 text-2xl font-semibold text-error-600 dark:text-error-400">
                {batch.failed_runs}
              </div>
            </div>

            {/* Pass Rate */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('pages.simulations.batch_details.pass_rate')}
              </div>
              {batch.completed_runs > 0 ? (
                <PassRateBar passRate={passRate} size="lg" />
              ) : (
                <span className="text-2xl font-semibold text-gray-400">-</span>
              )}
            </div>
          </div>

          {/* Timestamps and Created By */}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
            <span>
              <strong>{t('pages.simulations.batch_details.created')}:</strong>{' '}
              {formatDate(batch.created_at)}
            </span>
            {createdByUser && (
              <span>
                <strong>{t('pages.simulations.batch_details.created_by')}:</strong>{' '}
                {createdByUser.name}
              </span>
            )}
            {batch.started_at && (
              <span>
                <strong>{t('pages.simulations.batch_details.started')}:</strong>{' '}
                {formatDate(batch.started_at)}
              </span>
            )}
            {batch.completed_at && (
              <span>
                <strong>{t('pages.simulations.batch_details.completed')}:</strong>{' '}
                {formatDate(batch.completed_at)}
              </span>
            )}
          </div>
        </div>

        {/* Runs Table */}
        <TableCard.Root size="sm">
          <TableCard.Header
            title={t('pages.simulations.batch_details.runs_title')}
            contentTrailing={
              <span className="text-sm text-gray-500">
                {batch.runs?.length ?? 0} {t('pages.simulations.batch_details.runs_count')}
              </span>
            }
          />

          <div className="overflow-auto">
            <Table aria-label={t('pages.simulations.batch_details.runs_title')}>
              <Table.Header>
                <Table.Head id="id" label={t('pages.simulations.runs.columns.id')} isRowHeader />
                <Table.Head id="scenario_id" label={t('pages.simulations.runs.columns.scenario')} />
                <Table.Head id="assistant_id" label={t('pages.simulations.runs.columns.assistant')} />
                <Table.Head id="status" label={t('pages.simulations.runs.columns.status')} />
                <Table.Head id="overall_passed" label={t('pages.simulations.runs.columns.result')} />
                <Table.Head id="duration_seconds" label={t('pages.simulations.runs.columns.duration')} />
              </Table.Header>

              <AriaTableBody>
                {(batch.runs ?? []).map((run) => (
                  <Table.Row
                    key={run.id}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onAction={() => handleRunClick(run)}
                  >
                    <Table.Cell className="font-medium">#{run.id}</Table.Cell>
                    <Table.Cell>{getScenarioName(run.scenario_id)}</Table.Cell>
                    <Table.Cell>{getAssistantName(run.assistant_id)}</Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={run.status} />
                    </Table.Cell>
                    <Table.Cell>
                      <PassFailBadge passed={run.overall_passed} />
                    </Table.Cell>
                    <Table.Cell>
                      {run.duration_seconds != null
                        ? `${run.duration_seconds.toFixed(1)}s`
                        : '-'}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </AriaTableBody>
            </Table>

            {/* Empty state */}
            {(!batch.runs || batch.runs.length === 0) && (
              <div className="flex items-center justify-center py-12 text-gray-500">
                {t('pages.simulations.batch_details.no_runs')}
              </div>
            )}
          </div>
        </TableCard.Root>
      </div>
    </div>
  )
}

export default SimBatchDetailPage
